import { Stack } from "@mui/material";
import { ReactNode, useMemo } from "react";

import { FileItem } from "@/common/fileItem";
import { FileHelper } from "@/common/helpers/file";
import { GeneralAttachmentDto } from "@/core/api/generated/v1";

import AppChip, { AppChipProps } from "../Chip/AppChip";
import AppIcon from "../Icons/AppIcon";
import AppPopover from "../Popover/AppPopover";
import AppPopoverContent from "../Popover/AppPopoverContent";
import AppTypography from "../Text/AppTypography";
import FileListView from "./FileListView";

export interface Props {
  attachments: GeneralAttachmentDto[];
  noDataPlaceholder?: ReactNode;
  chipProps?: Partial<AppChipProps>;
}

export default function AttachmentsBriefInfoChip({
  attachments,
  noDataPlaceholder,
  chipProps,
}: Props) {
  const files = useMemo(() => FileItem.createManyFrom(attachments || []), [attachments]);
  const images = useMemo(
    () => files.filter((file) => FileHelper.getFileType(file.mimeType).isImage),
    [files],
  );
  const otherFiles = useMemo(
    () => files.filter((file) => !FileHelper.getFileType(file.mimeType).isImage),
    [files],
  );

  return (
    <AppPopover
      hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
      trigger={
        <AppChip
          variant='filled'
          size='medium'
          color='default'
          clickable={false}
          {...chipProps}
          label={
            <Stack direction='row' gap={0.5}>
              {images.length !== 0 && (
                <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
                  <AppIcon of='image' fontSize='medium' />
                  <AppTypography variant='body2' color='text.secondary'>
                    {images.length}
                  </AppTypography>
                </Stack>
              )}
              {otherFiles.length !== 0 && (
                <Stack direction='row' gap={0.25} sx={{ alignItems: "center" }}>
                  <AppIcon of='file' fontSize='medium' />
                  <AppTypography variant='body2' color='text.secondary'>
                    {otherFiles.length}
                  </AppTypography>
                </Stack>
              )}
              {images.length === 0 && otherFiles.length === 0 && (
                <AppTypography variant='body2' color='text.secondary'>
                  {noDataPlaceholder || "-"}
                </AppTypography>
              )}
            </Stack>
          }
        />
      }
    >
      <AppPopoverContent sx={{ maxWidth: 400 }}>
        <FileListView files={files} maxVisibleFiles={10} itemWidth={80} itemHeight={60} />
      </AppPopoverContent>
    </AppPopover>
  );
}
